import { useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { Box, BoxProps } from 'reflexbox';
import styled, { css } from 'styled-components';
import { mix, rgba } from 'polished';

import { CheckmarkIcon, CloseIcon, DashIcon } from '@treams/treams-icons';

import { Confetti } from './Confetti';

const ContainerChecked = css`
	background: ${(props) => mix(0.75, props.theme.colors.white, props.theme.colors.green)};
	border-color: ${(props) => mix(0.75, props.theme.colors.white, props.theme.colors.green)};
`;

const ContainerCheckedInteractive = css`
	&:hover {
		background: ${(props) => mix(0.5, props.theme.colors.white, props.theme.colors.green)};
		border-color: ${(props) => mix(0.5, props.theme.colors.white, props.theme.colors.green)};

		&:active {
			background: ${(props) => mix(0.25, props.theme.colors.white, props.theme.colors.green)};
			border-color: ${(props) => mix(0.25, props.theme.colors.white, props.theme.colors.green)};
		}
	}

	&:focus {
		box-shadow: 0px 0px 0px 2px
			${(props) => rgba(mix(0.25, props.theme.colors.white, props.theme.colors.green), 0.25)};
	}
`;

const ContainerNormal = css`
	background: #f9f9f9;
`;

const ContainerNormalInteractive = css`
	&:hover {
		background: ${(props) => props.theme.colors.whiteBlueLight};
		border-color: ${(props) => props.theme.colors.lightBlueDark};

		&:active {
			background: ${(props) => props.theme.colors.lightBlueDark};
			border-color: ${(props) => props.theme.colors.lightBlueDark};

			&:focus {
				box-shadow: 0px 0px 0px 2px ${(props) => rgba(props.theme.colors.lightBlueDark, 0.5)};
			}
		}
	}

	&:focus {
		border-color: ${(props) => props.theme.colors.primary};
		box-shadow: 0px 0px 0px 2px ${(props) => rgba(props.theme.colors.primary, 0.25)};
	}
`;

const ContainerLate = css`
	background: ${(props) => mix(0.75, props.theme.colors.white, props.theme.colors.yellow)};
	border-color: ${(props) => mix(0.75, props.theme.colors.white, props.theme.colors.yellow)};
`;

const ContainerLateInteractive = css`
	&:hover {
		background: ${(props) => mix(0.5, props.theme.colors.white, props.theme.colors.yellow)};
		border-color: ${(props) => mix(0.5, props.theme.colors.white, props.theme.colors.yellow)};

		&:active {
			background: ${(props) => mix(0.25, props.theme.colors.white, props.theme.colors.yellow)};
			border-color: ${(props) => mix(0.25, props.theme.colors.white, props.theme.colors.yellow)};
		}
	}

	&:focus {
		box-shadow: 0px 0px 0px 2px
			${(props) => rgba(mix(0.25, props.theme.colors.white, props.theme.colors.yellow), 0.25)};
	}
`;

const Container = styled(Box)<{ checked?: boolean | null; interactive?: boolean; late?: boolean }>`
	width: 24px;
	height: 24px;
	border-radius: 4px;
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	box-sizing: border-box;

	${(props) => props.interactive && `cursor: pointer;`}

	${(props) => (props.checked ? ContainerChecked : props.late ? ContainerLate : ContainerNormal)}
	${(props) =>
		props.interactive &&
		(props.checked
			? ContainerCheckedInteractive
			: props.late
			? ContainerLateInteractive
			: ContainerNormalInteractive)}

	svg {
		display: block;
		width: 24px;
		height: 24px;
		margin: -1px;

		path {
			fill: ${(props) => (props.late ? props.theme.colors.yellow : props.theme.colors.green)};
		}
	}
`;

export function Todo(
	props: BoxProps & {
		checked?: boolean | null;
		late?: boolean;
		onChange?: (checked: boolean) => void;
	},
) {
	const [confetti, setConfetti] = useState<{ x: number; y: number } | undefined>(undefined);

	const ref = useRef<HTMLElement>(null);

	useUpdateEffect(() => {
		if (props.checked && ref.current) {
			const bounds = ref.current.getBoundingClientRect();

			setConfetti({
				x: (bounds.left + bounds.width / 2) / window.innerWidth,
				y: (bounds.top + bounds.height / 2) / window.innerHeight,
			});
		}
	}, [props.checked]);

	const icon =
		props.checked === null ? (
			<DashIcon />
		) : props.checked ? (
			<CheckmarkIcon />
		) : props.checked && props.late ? (
			<CloseIcon />
		) : (
			''
		);

	return (
		<>
			<Container
				ref={ref}
				{...props}
				tabIndex={0}
				interactive={!!props.onChange}
				onClick={props.onChange ? () => props.onChange?.(!props.checked) : undefined}
			>
				{icon}
			</Container>
			{confetti && <Confetti origin={confetti} onFinish={() => setConfetti(undefined)} />}
		</>
	);
}
